import { Effect, EffectAttribute } from 'postprocessing'
import React, { FC, forwardRef, useMemo } from 'react'
import { Uniform } from 'three'

const NEIGHBOUR_COUNT = 8

// adapted from the EDL shader code from Christian Boucheny in cloud compare:
// https://github.com/cloudcompare/trunk/tree/master/plugins/qEDL/shaders/EDL
// Adapted for postprocessing library
const NEW_EDL_FRAGMENT_SHADER = `
#define NEIGHBOUR_COUNT ${NEIGHBOUR_COUNT}

uniform float radius; // Pixel radius
uniform float strength;

// Provided by postprocessing when EffectAttribute.DEPTH is used
// uniform sampler2D depthBuffer;
// uniform vec2 texelSize; // vec2(1.0 / screenWidth, 1.0 / screenHeight)

const vec2 NEIGHBOURS[NEIGHBOUR_COUNT] = vec2[](
  vec2(-1.0, -1.0), vec2( 0.0, -1.0), vec2( 1.0, -1.0),
  vec2(-1.0,  1.0), vec2( 0.0,  1.0), vec2( 1.0,  1.0),
  vec2(-1.0,  0.0), vec2( 1.0,  0.0)
);

float response(float depth, vec2 uv) {
    vec2 uvRadius = radius * texelSize; // Pixel radius scaled by texel size
    float sum = 0.0;

    for(int i = 0; i < NEIGHBOUR_COUNT; i++) {
        vec2 uvNeighbor = uv + uvRadius * NEIGHBOURS[i];
        float neighbourDepth = texture2D(depthBuffer, uvNeighbor).r;

        // Assuming standard depth buffer (0=near, 1=far)
        // Accumulate depth difference for closer neighbors
        if (neighbourDepth < depth) {
             // Logic from new shader proposal: sum += max(0.0, depth - neighbourDepth);
             // Adjust strength multiplier as needed. Original proposal had 300.0 * strength.
             // Using strength directly here requires higher values (e.g., 50-300) compared to the previous shader (e.g., 1.0).
             sum += max(0.0, depth - neighbourDepth);
        }
    }
    // Adjust the division factor if needed, original proposal divided by NEIGHBOUR_COUNT
    return sum; // Returning raw sum, scaling happens in mainImage
}

void mainUv(inout vec2 uv) {
  // pass-through
}

void mainImage(in vec4 inputColor, in vec2 uv, out vec4 outputColor) {
    float depth = texture2D(depthBuffer, uv).r;
    vec4 sceneColor = inputColor;

    // Handle background/far plane
    if (depth >= 1.0) {
        outputColor = sceneColor;
        return;
    }

    float res = response(depth, uv);
    // Apply exponential shading based on response and strength
    // Adjust the multiplier (e.g., 300.0) as needed based on scene scale and desired effect intensity
    float shadeFactor = exp(-res * strength * 300.0 / float(NEIGHBOUR_COUNT)); // Added division by count and multiplier back

    outputColor = vec4(sceneColor.rgb * shadeFactor, sceneColor.a);
}
`

interface EDLEffectOptions {
  radius?: number
  strength?: number
}

class EDLEffectImpl extends Effect {
  constructor({ radius = 1.0, strength = 1.0 }: EDLEffectOptions = {}) {
    super('EDLEffect', NEW_EDL_FRAGMENT_SHADER, {
      uniforms: new Map([
        ['radius', new Uniform(radius)],
        ['strength', new Uniform(strength)],
      ]),
      attributes: EffectAttribute.DEPTH,
    })
  }

  setRadius(val: number) {
    ;(this.uniforms.get('radius') as Uniform).value = val
  }
  setstrength(val: number) {
    ;(this.uniforms.get('strength') as Uniform).value = val
  }
}

interface EDLEffectProps {
  radius?: number
  strength?: number
}

export const EDLEffect: FC<EDLEffectProps> = forwardRef<
  EDLEffectImpl,
  EDLEffectProps
>(function EDLEffect({ radius = 1.0, strength = 1.0 }, ref) {
  const effect = useMemo(
    () => new EDLEffectImpl({ radius, strength }),
    [radius, strength]
  )
  return <primitive ref={ref} object={effect} dispose={null} />
})
